import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../i18n';
import { PropertyCard } from '../components/property/PropertyCard';
import { CompanyReviews, CompanyRatingBadge } from '../components/company/CompanyReviews';
import { client } from '../api/client';
import { Property, AdKind, AdType, PropertyCategory } from '../types';

interface CompanyProfile {
  id: string;
  name: string;
  username: string;
  logo: string | null;
  description: string | null;
  email: string;
  phone: string;
  memberSince: string;
  reviewStats?: {
    average: number;
    total: number;
  };
  stats: {
    totalListings: number;
    ponuda: number;
    potraznja: number;
    aukcija: number;
    prodaja: number;
    izdavanje: number;
  };
}

interface CompanyListing {
  id: string;
  title: string;
  slug: string;
  price: number;
  size: number;
  rooms: string;
  propertyType: string;
  listingType: string;
  adType: string;
  city: string;
  municipality: string;
  primaryImage: string;
  createdAt: string;
  featured: boolean;
  views: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type AdTypeFilter = 'all' | 'ponuda' | 'potraznja' | 'aukcija';
type SortOption = 'newest' | 'oldest' | 'price_asc' | 'price_desc';

export function CompanyProfilePage() {
  const { username } = useParams<{ username: string }>();
  const { language } = useI18n();
  const location = useLocation();
  const reviewsRef = useRef<HTMLDivElement>(null);
  
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [listings, setListings] = useState<CompanyListing[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingListings, setLoadingListings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [adTypeFilter, setAdTypeFilter] = useState<AdTypeFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [currentPage, setCurrentPage] = useState(1);

  // Load company profile
  useEffect(() => {
    const fetchCompany = async () => {
      if (!username) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const res = await client.api.fetch(`/api/company/${username}`);
        const data = await res.json();
        
        if (data.success && data.company) {
          setCompany(data.company);
        } else {
          setError(data.error || 'Kompanija nije pronađena');
        }
      } catch (err) {
        console.error('Error fetching company:', err);
        setError('Greška pri učitavanju profila');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompany();
  }, [username]);

  // Load listings when filters change
  useEffect(() => {
    const fetchListings = async () => {
      if (!username) return;
      
      setLoadingListings(true);
      
      try {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: '10',
          sort: sortBy,
          ...(adTypeFilter !== 'all' && { adType: adTypeFilter })
        });
        
        const res = await client.api.fetch(`/api/company/${username}/listings?${params}`);
        const data = await res.json();
        
        if (data.success) {
          setListings(data.data || []);
          setPagination(data.pagination);
        }
      } catch (err) {
        console.error('Error fetching listings:', err);
      } finally {
        setLoadingListings(false);
      }
    };
    
    fetchListings();
  }, [username, adTypeFilter, sortBy, currentPage]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [adTypeFilter, sortBy]);

  // Scroll to reviews if hash is #reviews
  useEffect(() => {
    if (location.hash === '#reviews' && reviewsRef.current) {
      setTimeout(() => {
        reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [location.hash, loading]);

  // Transform listing for PropertyCard
  const transformListing = (listing: CompanyListing): Property => ({
    id: listing.id,
    title: listing.title,
    description: '',
    price: listing.price,
    currency: 'EUR' as const,
    adKind: (listing.adType === 'ponuda' ? 'Ponuda' : listing.adType === 'potraznja' ? 'Potražnja' : 'Aukcija') as AdKind,
    adType: (listing.listingType === 'prodaja' ? 'Prodaja' : 'Izdavanje') as AdType,
    category: listing.propertyType as PropertyCategory,
    squareMeters: listing.size,
    rooms: parseInt(listing.rooms) || 0,
    region: '',
    city: listing.city,
    location: { region: '', city: listing.city, address: listing.municipality },
    images: listing.primaryImage ? [listing.primaryImage] : [],
    features: {},
    contactPhone: '',
    views: listing.views || 0,
    contactViews: 0,
    status: 'active' as const,
    approved: true,
    featured: listing.featured,
    createdAt: listing.createdAt,
    userId: ''
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8">
            <svg className="w-16 h-16 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {language === 'sr' ? 'Kompanija nije pronađena' : 'Company not found'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {error || (language === 'sr' ? 'Profil kompanije ne postoji ili nije javno dostupan.' : 'Company profile does not exist or is not publicly available.')}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {language === 'sr' ? 'Nazad na početnu' : 'Back to home'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const adTypeButtons: { key: AdTypeFilter; labelSr: string; labelEn: string; count: number }[] = [
    { key: 'all', labelSr: 'Svi oglasi', labelEn: 'All listings', count: company.stats.totalListings },
    { key: 'ponuda', labelSr: 'Ponuda', labelEn: 'Offers', count: company.stats.ponuda },
    { key: 'potraznja', labelSr: 'Potražnja', labelEn: 'Demands', count: company.stats.potraznja },
    { key: 'aukcija', labelSr: 'Aukcija', labelEn: 'Auctions', count: company.stats.aukcija },
  ];

  const sortOptions: { value: SortOption; labelSr: string; labelEn: string }[] = [
    { value: 'newest', labelSr: 'Najnovije', labelEn: 'Newest' },
    { value: 'oldest', labelSr: 'Najstarije', labelEn: 'Oldest' },
    { value: 'price_asc', labelSr: 'Cena ↑', labelEn: 'Price ↑' },
    { value: 'price_desc', labelSr: 'Cena ↓', labelEn: 'Price ↓' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Company Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden mb-8"
        >
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Logo */}
              <div className="flex-shrink-0">
                {company.logo ? (
                  <img
                    src={company.logo.startsWith('http') ? company.logo : `${import.meta.env.VITE_API_URL?.replace('/api', '') || ''}/${company.logo}`}
                    alt={company.name}
                    className="w-24 h-24 md:w-32 md:h-32 object-contain rounded-xl bg-slate-100 dark:bg-slate-700"
                  />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 bg-amber-500 rounded-xl flex items-center justify-center text-slate-900 text-4xl md:text-5xl font-bold">
                    {company.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
                    {company.name}
                  </h1>
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-sm font-medium rounded-full">
                    {language === 'sr' ? 'Pravno lice' : 'Legal entity'}
                  </span>
                </div>
                
                {/* Rating badge */}
                {company.reviewStats && company.reviewStats.total > 0 && (
                  <div className="mb-2">
                    <CompanyRatingBadge
                      average={company.reviewStats.average}
                      total={company.reviewStats.total}
                      onClick={() => reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    />
                  </div>
                )}
                
                {company.description && (
                  <p className="text-slate-600 dark:text-slate-400 mb-4 max-w-2xl">
                    {company.description}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>
                      {language === 'sr' ? 'Član od' : 'Member since'}{' '}
                      {new Date(company.memberSince).toLocaleDateString(language === 'sr' ? 'sr-RS' : 'en-US', { year: 'numeric', month: 'long' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>
                      {company.stats.totalListings} {language === 'sr' ? 'aktivnih oglasa' : 'active listings'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Stats Bar */}
          <div className="border-t border-slate-200 dark:border-slate-700 px-6 md:px-8 py-4 bg-slate-50 dark:bg-slate-800/50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-amber-500">{company.stats.ponuda}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Ponuda' : 'Offers'}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-500">{company.stats.potraznja}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Potražnja' : 'Demands'}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-500">{company.stats.aukcija}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Aukcije' : 'Auctions'}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-500">{company.stats.prodaja + company.stats.izdavanje}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Ukupno' : 'Total'}</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Ad Type Tabs */}
            <div className="flex flex-wrap gap-2">
              {adTypeButtons.map(btn => (
                <button
                  key={btn.key}
                  onClick={() => setAdTypeFilter(btn.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    adTypeFilter === btn.key
                      ? 'bg-amber-500 text-slate-900'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {language === 'sr' ? btn.labelSr : btn.labelEn}
                  <span className="ml-2 text-xs opacity-75">({btn.count})</span>
                </button>
              ))}
            </div>
            
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 border-0 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {language === 'sr' ? opt.labelSr : opt.labelEn}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Listings Grid */}
        {loadingListings ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {language === 'sr' ? 'Nema oglasa' : 'No listings'}
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {language === 'sr' 
                ? 'Ova kompanija trenutno nema aktivnih oglasa u ovoj kategoriji.' 
                : 'This company currently has no active listings in this category.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {listings.map((listing, index) => (
                <motion.div
                  key={listing.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <PropertyCard property={transformListing(listing)} />
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum = i + 1;
                    if (pagination.totalPages > 5) {
                      if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-amber-500 text-slate-900'
                            : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={currentPage === pagination.totalPages}
                  className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}

        {/* Reviews Section */}
        <div ref={reviewsRef} className="mt-8">
          <CompanyReviews
            companyUsername={company.username}
            companyName={company.name}
            companyId={company.id}
          />
        </div>
      </div>
    </div>
  );
}
